import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import { QueryState } from "@/components/common/AsyncState";
import { createBranch, fetchBranches, updateBranch } from "@/api/branches";
import { formatMoney } from "@/utils/format";
import type { Branch } from "@/types/branch";

function BranchDialog({ open, onClose, branch }: { open: boolean; onClose: () => void; branch?: Branch }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(branch?.name ?? "");
  const [address, setAddress] = useState(branch?.address ?? "");
  const [phone, setPhone] = useState(branch?.phone ?? "");
  const [planRevenue, setPlanRevenue] = useState(branch?.planRevenue ?? 0);
  const [active, setActive] = useState(branch?.active ?? true);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { name, address, phone, planRevenue, active };
      return branch ? updateBranch(branch.id, payload) : createBranch(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["branches"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{branch ? "Редактирование филиала" : "Новый филиал"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Название" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField label="Адрес" value={address} onChange={(e) => setAddress(e.target.value)} />
          <TextField label="Телефон" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <TextField label="План по выручке" type="number" value={planRevenue} onChange={(e) => setPlanRevenue(Number(e.target.value))} />
          <FormControlLabel control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />} label="Активен" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" disabled={!name || mutation.isPending} onClick={() => mutation.mutate()}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function BranchesTab() {
  const { data, isLoading, error } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const [dialogState, setDialogState] = useState<{ open: boolean; branch?: Branch }>({ open: false });

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogState({ open: true })}>
          Новый филиал
        </Button>
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Адрес</TableCell>
              <TableCell>Телефон</TableCell>
              <TableCell>План</TableCell>
              <TableCell>Активен</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((branch) => (
              <TableRow key={branch.id}>
                <TableCell>{branch.name}</TableCell>
                <TableCell>{branch.address}</TableCell>
                <TableCell>{branch.phone}</TableCell>
                <TableCell>{formatMoney(branch.planRevenue)}</TableCell>
                <TableCell>{branch.active ? "Да" : "Нет"}</TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<EditIcon />} onClick={() => setDialogState({ open: true, branch })}>
                    Изменить
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </QueryState>
      <BranchDialog open={dialogState.open} branch={dialogState.branch} onClose={() => setDialogState({ open: false })} />
    </>
  );
}
